import React from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

type CustomDatePickerProps = {
  timeZone?: string;
  dateFormat?: string;
} & Omit<DatePickerProps, 'format' | 'cellRender' | 'renderExtraFooter'>;

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  timeZone,
  className = 'custom-picker',
  showToday = false,
  dateFormat = 'ddd, MMM D',
  disabled = false,
  size = 'middle',
  placeholder,
  suffixIcon,
  ...rest
}) => {
  const defaultFooter = () => {
    const tz = timeZone || dayjs.tz.guess();
    const todayInTZ = dayjs().tz(tz);
    return (
      <a
        style={{ display: 'block', textAlign: 'center', padding: '4px 0' }}
        onClick={() => onChange?.(todayInTZ, todayInTZ.format('YYYY-MM-DD'))}
      >
        Today
      </a>
    );
  };

  const defaultCellRender: DatePickerProps['cellRender'] = (current, info) => {
    // To avoid dispaly date instead month/year,Apply the custom today highlight only to the date panel.
    if (info.type !== 'date') return info.originNode;

    const currentDate = current as Dayjs;
    const tz = timeZone || dayjs.tz.guess();
    const todayInTZ = dayjs().tz(tz);
    const isToday =
      currentDate.format('YYYY-MM-DD') === todayInTZ.format('YYYY-MM-DD');
    return (
      <div
        className={`ant-picker-cell-inner${isToday ? ' ant-picker-cell-today-custom' : ''}`}
        style={
          isToday
            ? {
                border: '1px solid var(--info)',
                borderRadius: '4px',
              }
            : undefined
        }
      >
        {currentDate.date()}
      </div>
    );
  };

  return (
    <DatePicker
      {...rest}
      value={value}
      onChange={onChange}
      className={className}
      format={dateFormat}
      suffixIcon={suffixIcon}
      showToday={showToday}
      disabled={disabled}
      size={size}
      placeholder={placeholder}
      renderExtraFooter={defaultFooter}
      cellRender={defaultCellRender}
    />
  );
};

export default CustomDatePicker;
